import classnames from 'classnames';
import React, {
    ComponentPropsWithRef,
    ElementType,
    ReactElement,
    ReactNode,
    forwardRef,
    useMemo,
} from 'react';
import { InView } from 'react-intersection-observer';

import { Spinner, SpinnerSize } from '../Spinner';
import { mergeRefs } from '../../lib/ref';

import styles from './Button.module.css';

export type ButtonSize = 32 | 44 | 48 | 52 | 60;

export type ButtonVariant =
    | 'accent'
    | 'accentWhite'
    | 'custom'
    | 'secondary'
    | 'secondaryTransparent'
    | 'plus'
    | 'primary';

export type ButtonTheme = 'light' | 'dark';

type SpinnerColor = 'white' | 'black';

const SPINNER_SIZE_MAP: Record<ButtonSize, SpinnerSize> = {
    32: SpinnerSize.XSmall,
    44: SpinnerSize.MEDIUM,
    48: SpinnerSize.MEDIUM,
    52: SpinnerSize.MEDIUM,
    60: SpinnerSize.MEDIUM,
} as const;

const SPINNER_COLOR_MAP: Record<ButtonVariant, SpinnerColor> = {
    accent: 'white',
    accentWhite: 'black',
    custom: 'white',
    secondary: 'white',
    secondaryTransparent: 'white',
    plus: 'white',
    primary: 'white',
} as const;

const SIZE_CLASS_MAP: Record<ButtonSize, string> = {
    32: styles.size32,
    44: styles.size44,
    48: styles.size48,
    52: styles.size52,
    60: styles.size60,
} as const;

const VARIANT_CLASS_MAP: Record<ButtonVariant, string> = {
    accent: styles.accent,
    accentWhite: styles.accentWhite,
    custom: styles.custom,
    secondary: styles.secondary,
    secondaryTransparent: styles.secondaryTransparent,
    plus: styles.plus,
    primary: styles.primary,
};

const THEME_CLASS_MAP: Record<ButtonTheme, string> = {
    light: styles.light,
    dark: styles.dark,
};

type PolymorphicRef<T extends ElementType> = ComponentPropsWithRef<T>['ref'];

interface BaseButtonProps {
    variant?: ButtonVariant;
    theme?: ButtonTheme;
    size?: ButtonSize;
    startIcon?: ReactNode;
    isWithoutPaddings?: boolean;
    isFullWidth?: boolean;
    isDisabled?: boolean;
    isLoading?: boolean;
    onShow?: () => void;
}

type ButtonProps<T extends ElementType = 'button'> = BaseButtonProps &
    Omit<ComponentPropsWithRef<T>, keyof BaseButtonProps | 'as'> & {
        as?: T;
    };

type PolymorphicComponent = <T extends ElementType = 'button'>(
    props: ButtonProps<T> & { ref?: PolymorphicRef<T> }
) => ReactElement | null;

export const Button = forwardRef(
    <T extends ElementType = 'button'>(
        {
            as,
            children,
            theme = 'light',
            variant = 'primary',
            size = 32,
            startIcon,
            isWithoutPaddings,
            isFullWidth,
            className,
            isDisabled,
            isLoading,
            onShow,
            ...restProps
        }: ButtonProps<T>,
        ref: PolymorphicRef<T>
    ) => {
        const Component = as || 'button';

        const hasIconOnly = !children && Boolean(startIcon);

        const buttonContent = useMemo(
            () => (
                <>
                    {startIcon && <span className={styles.startIcon}>{startIcon}</span>}
                    {children}
                </>
            ),
            [startIcon, children]
        );

        return (
            <InView
                triggerOnce
                threshold={0.8}
                skip={!onShow}
                onChange={(inView) => {
                    if (inView) {
                        onShow?.();
                    }
                }}
            >
                {({ ref: inViewRef }) => (
                    <Component
                        ref={mergeRefs(ref, inViewRef)}
                        className={classnames(
                            styles.button,
                            SIZE_CLASS_MAP[size],
                            VARIANT_CLASS_MAP[variant],
                            THEME_CLASS_MAP[theme],
                            className,
                            {
                                [styles.withStartIcon]: Boolean(startIcon),
                                [styles.onlyIcon]: hasIconOnly,
                                [styles.withoutPaddings]: isWithoutPaddings,
                                [styles.fullWidth]: isFullWidth,
                                [styles.disabled]: isDisabled,
                            }
                        )}
                        disabled={isDisabled}
                        {...restProps}
                    >
                        {isLoading ? (
                            <div>
                                <div className={styles.loader}>{buttonContent}</div>
                                <Spinner
                                    size={SPINNER_SIZE_MAP[size]}
                                    circleClassName={
                                        styles[`spinnerCircle_${SPINNER_COLOR_MAP[variant]}`]
                                    }
                                />
                            </div>
                        ) : (
                            buttonContent
                        )}
                    </Component>
                )}
            </InView>
        );
    }
) as PolymorphicComponent & {
    displayName?: string;
};

Button.displayName = 'Button';
